import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useProjects } from "@/contexts/ProjectContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import KpiCard from "@/components/dashboard/KpiCard";
import PageHeader from "@/components/dashboard/PageHeader";
import StatusBadge from "@/components/dashboard/StatusBadge";
import EmptyState from "@/components/dashboard/EmptyState";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, AreaChart, Area, CartesianGrid,
  BarChart, Bar,
} from "recharts";
import { Sparkles, X } from "lucide-react";

export default function DashboardHome() {
  const { user } = useAuth();
  const { activeProjectId, activeProject } = useProjects();
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [showWelcome, setShowWelcome] = useState(true);

  useEffect(() => {
    if (!activeProjectId) return;
    api.get(`/dashboard/${activeProjectId}`).then(({ data }) => setData(data));
    api.get(`/tasks/${activeProjectId}`).then(({ data }) => setTasks(data));
  }, [activeProjectId]);

  if (!activeProjectId) return <DashboardLayout><p className="text-muted-foreground">Loading project…</p></DashboardLayout>;

  const term = data?.terminology || {};
  const trafficSeries = (data?.traffic_series || []).slice(-14).map((d) => ({ value: d.views }));
  const revenueSparkRec = (data?.revenue_series || []).slice(-14).map((d) => ({ value: d.recurring }));
  const revenueSparkOne = (data?.revenue_series || []).slice(-14).map((d) => ({ value: d.one_time }));

  const contentBuckets = (() => {
    const buckets = [{ week: "W-4" }, { week: "W-3" }, { week: "W-2" }, { week: "W-1" }, { week: "W0" }];
    const series = data?.traffic_series || [];
    series.forEach((s, i) => {
      const w = Math.floor(((series.length - 1 - i)) / 7);
      const idx = 4 - Math.min(4, w);
      buckets[idx].published = (buckets[idx].published || 0) + ((i % 3 === 0) ? 1 : 0);
    });
    return buckets;
  })();

  const prov = data?.data_provenance || {};
  const demoTag = prov.sales_metrics === "demo" ? " · demo" : "";
  const simTag = " · simulated";

  return (
    <DashboardLayout>
      {data?.shopify_live && (
        <div className="bg-emerald-50 border border-emerald-500/40 rounded-sm p-5 mb-4" data-testid="shopify-live-kpis" data-provenance={prov.shopify || "live"}>
          <div className="flex items-center justify-between">
            <div>
              <p className="editorial-overline">Shopify · Live</p>
              <p className="font-serif text-lg mt-1">{data.shopify_live.shop_name} <span className="text-xs font-mono text-emerald-800/70">{data.shopify_live.shop_domain}</span></p>
            </div>
            <p className="text-[11px] text-emerald-800/70">Last sync: {data.shopify_live.last_sync_at ? new Date(data.shopify_live.last_sync_at).toLocaleString() : "pending"}</p>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><p className="text-[10px] uppercase tracking-widest text-emerald-800/70">Products</p><p className="font-serif text-2xl">{data.shopify_live.product_count ?? 0}<span className="text-xs font-mono text-emerald-800/70 ml-1">{data.shopify_live.active_product_count != null ? `· ${data.shopify_live.active_product_count} active` : ""}</span></p></div>
            <div><p className="text-[10px] uppercase tracking-widest text-emerald-800/70">Variants</p><p className="font-serif text-2xl">{data.shopify_live.variant_count ?? 0}</p></div>
            <div><p className="text-[10px] uppercase tracking-widest text-emerald-800/70">Inventory on hand</p><p className="font-serif text-2xl">{(data.shopify_live.total_inventory_available ?? 0).toLocaleString()}</p></div>
            <div><p className="text-[10px] uppercase tracking-widest text-emerald-800/70">Low / Out of stock</p><p className="font-serif text-2xl">{data.shopify_live.low_stock_variants ?? 0}<span className="text-xs font-mono text-emerald-800/70 ml-1">/ {data.shopify_live.out_of_stock_variants ?? 0}</span></p></div>
          </div>
          {data.shopify_live.last_sync_error && (
            <p className="mt-3 text-[11px] text-amber-800/90" data-testid="shopify-partial-sync-warning">
              Partial sync warning: {data.shopify_live.last_sync_error}
            </p>
          )}
        </div>
      )}
      {data?.data_source === "demo" && (
        <div className="mb-4 px-4 py-2.5 bg-amber-50 border border-amber-500/40 text-amber-800 rounded-sm text-sm flex items-center justify-between" data-testid="demo-data-banner">
          <span>{data.data_source_note}</span>
          <a href="/integrations" className="text-xs uppercase tracking-widest hover:text-amber-900 underline" data-testid="demo-data-connect">Connect integrations →</a>
        </div>
      )}
      {showWelcome && (
        <div className="bg-navy-900 text-ivory-50 rounded-sm p-5 sm:p-6 mb-6 flex items-start justify-between gap-4 relative overflow-hidden" data-testid="welcome-banner">
          <div className="flex gap-4">
            <Sparkles className="text-gold-400 shrink-0 mt-1" size={20} />
            <div>
              <p className="editorial-overline !text-gold-400">Welcome back, {user?.name?.split(" ")[0]}</p>
              <h2 className="font-serif text-xl sm:text-2xl mt-2">Compounding starts with the next published page.</h2>
              <p className="text-sm text-ivory-200/70 mt-2 max-w-2xl leading-relaxed">
                Generate this week&rsquo;s growth plan, ship one Article Brief, and approve one piece in your content calendar. Three small acts, one compounding engine.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/growth-plan" className="px-4 py-2 bg-gold-500 hover:bg-gold-400 text-navy-900 rounded-sm text-xs font-semibold transition-colors" data-testid="welcome-cta-plan">
                  Generate growth plan
                </Link>
                <Link to="/articles" className="px-4 py-2 border border-ivory-200/20 hover:border-gold-500 text-ivory-50 hover:text-gold-500 rounded-sm text-xs font-semibold transition-colors" data-testid="welcome-cta-article">
                  Start an article
                </Link>
                <Link to="/content-calendar" className="px-4 py-2 border border-ivory-200/20 hover:border-gold-500 text-ivory-50 hover:text-gold-500 rounded-sm text-xs font-semibold transition-colors" data-testid="welcome-cta-calendar">
                  Open calendar
                </Link>
              </div>
            </div>
          </div>
          <button onClick={() => setShowWelcome(false)} className="text-ivory-200/60 hover:text-ivory-50" data-testid="welcome-dismiss" aria-label="Dismiss">
            <X size={16} />
          </button>
        </div>
      )}

      <PageHeader
        overline={activeProject?.name || "Project"}
        title="Growth dashboard"
        description={`Compounding pipeline for ${activeProject?.website_url || "your project"}. Adapted to ${activeProject?.business_model || "your business model"}.`}
        actions={
          <Link to="/growth-plan" className="px-4 py-2 bg-navy-900 text-ivory-50 hover:bg-navy-800 rounded-sm text-sm font-semibold transition-colors" data-testid="dashboard-generate-plan">
            Generate Growth Plan
          </Link>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="kpi-grid" data-sales-provenance={prov.sales_metrics || "demo"}>
        <KpiCard testId="kpi-organic-traffic" label={term.primary_traffic_label || "Organic traffic"} value={(data?.kpis?.organic_traffic_estimate || 0).toLocaleString()} sub={`last 30d${demoTag}`} accent sparkline={trafficSeries} sparkColor="#C5A059" />
        <KpiCard testId="kpi-revenue-recurring" label={term.revenue_recurring_label || "Recurring revenue"} value={`$${(data?.kpis?.revenue_recurring || 0).toLocaleString()}`} sub={`last 30d${demoTag}`} sparkline={revenueSparkRec} sparkColor="#0A1128" />
        <KpiCard testId="kpi-revenue-one-time" label={term.revenue_one_time_label || "One-time revenue"} value={`$${(data?.kpis?.revenue_one_time || 0).toLocaleString()}`} sub={`last 30d${demoTag}`} sparkline={revenueSparkOne} sparkColor="#9CAE96" />
        <KpiCard testId="kpi-ai-visibility" label="AI visibility" value={`${data?.kpis?.ai_visibility_score ?? 0} / 100`} sub="avg across simulated prompts" />
        <KpiCard testId="kpi-orders" label={term.top_kpi_a || "Conversions"} value={(data?.kpis?.orders ?? 0).toLocaleString()} sub={demoTag ? "demo" : undefined} />
        <KpiCard testId="kpi-signups" label={term.signup_label || "Signups"} value={(data?.kpis?.signups ?? 0).toLocaleString()} sub={demoTag ? "demo" : undefined} />
        <KpiCard testId="kpi-keywords" label="Keywords tracked" value={data?.kpis?.keyword_opportunities ?? 0} />
        <KpiCard testId="kpi-content-published" label="Content published" value={data?.kpis?.content_published_this_month ?? 0} sub="this month" />
        <KpiCard testId="kpi-conversion-rate" label="Conversion rate" value={`${data?.kpis?.conversion_rate ?? 0}%`} sub={demoTag ? "demo" : undefined} />
        <KpiCard testId="kpi-seo-health" label="SEO health" value={`${data?.kpis?.seo_health_score ?? 0}`} sub={`audit-derived${simTag}`} />
        <KpiCard testId="kpi-authority" label="Authority score" value={data?.kpis?.authority_score ?? 0} sub="simulated" />
        <KpiCard testId="kpi-tasks" label="Pending tasks" value={data?.kpis?.pending_tasks ?? 0} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 bg-white border border-border rounded-sm p-6">
          <p className="editorial-overline">Traffic{demoTag}</p>
          <h3 className="font-serif text-xl mt-1 mb-4">30-day organic sessions</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.traffic_series || []}>
                <defs>
                  <linearGradient id="ggold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C5A059" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#C5A059" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E5E0D5" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#4A5568" fontSize={11} />
                <YAxis stroke="#4A5568" fontSize={11} />
                <Tooltip contentStyle={{ border: "1px solid #C5A059", borderRadius: 2, fontSize: 12 }} />
                <Area type="monotone" dataKey="views" stroke="#C5A059" strokeWidth={2} fill="url(#ggold)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-border rounded-sm p-6">
          <p className="editorial-overline">Revenue mix{demoTag}</p>
          <h3 className="font-serif text-xl mt-1 mb-4">Recurring vs one-time</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.revenue_series || []}>
                <CartesianGrid stroke="#E5E0D5" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#4A5568" fontSize={11} />
                <YAxis stroke="#4A5568" fontSize={11} />
                <Tooltip contentStyle={{ border: "1px solid #C5A059", borderRadius: 2, fontSize: 12 }} />
                <Line type="monotone" dataKey="recurring" stroke="#0A1128" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="one_time" stroke="#9CAE96" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="bg-white border border-border rounded-sm p-6">
          <p className="editorial-overline">Content production</p>
          <h3 className="font-serif text-xl mt-1 mb-4">Pieces shipped per week</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={contentBuckets}>
                <CartesianGrid stroke="#E5E0D5" vertical={false} />
                <XAxis dataKey="week" fontSize={11} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ border: "1px solid #C5A059", borderRadius: 2, fontSize: 12 }} />
                <Bar dataKey="published" fill="#0A1128" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-border rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="editorial-overline">Pipeline</p>
              <h3 className="font-serif text-xl mt-1">Pending growth tasks</h3>
            </div>
            <Link to="/tasks" className="text-xs text-gold-600 hover:text-gold-500 underline underline-offset-4" data-testid="see-all-tasks">All tasks →</Link>
          </div>
          {tasks.length === 0 ? (
            <EmptyState
              testId="dashboard-tasks-empty"
              title="No tasks yet"
              description="Tasks created from audits, content reviews, or growth plans will land here."
            />
          ) : (
            <ul className="divide-y divide-border">
              {tasks.slice(0, 7).map((t) => (
                <li key={t.id} className="py-3 flex items-center justify-between gap-3" data-testid={`task-row-${t.id}`}>
                  <span className="text-sm text-navy-900 truncate">{t.title}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{t.priority}</span>
                    <StatusBadge status={t.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
